import uuid from 'uuid/v4';
import { merge } from 'lodash-es';
import moment from 'moment';
import { createAction } from 'redux-actions';

import {
  getValueFromObjects,
  getAddressMapping,
  getAddressMappingV2,
  getCPRMapping,
  createParams,
  validateParams,
  decodeState,
  callApiPost,
  getTravelValue,
  callApiGet,
  getDefaultOrValueFromObjects,
  getOneYearActivationDate,
  getNext12MonthNamesWithYear,
  getPetActivationDate,
  getCurrentDate,
} from './common';

import Api from './api';
import { getPackagesWithCoverage } from '../package/packageUtils';
import * as types from '../../constants/Api';
import { createErrorBody } from '../error';
import { appErrorAction } from '../../actions/App';
import { actionHandler } from '../form/formUtils';

export const getEffectiveDate = (formValues, mappedParams) => {
  let effectiveDate = moment(getValueFromObjects('activationDate', mappedParams, formValues), 'DD-MM-YYYY').format('YYYY-MM-DD');
  if(formValues.forProduct) {
    effectiveDate = moment(getValueFromObjects(`activationDate-${formValues.forProduct}`, mappedParams, formValues), 'DD-MM-YYYY').format('YYYY-MM-DD');
  }
  if(formValues['seperateActivationDate'] === 'false' && formValues['activationDate-combined']) {
    effectiveDate = moment(getValueFromObjects('activationDate-combined', mappedParams, formValues), 'DD-MM-YYYY').format('YYYY-MM-DD');
  }
  return effectiveDate;
}

export const getPurchaseMapping = (formValues, mappedParams, product, type) => {
  const effectiveDate = getEffectiveDate(formValues, mappedParams);
  const chosenPackage = getChosenPackage(mappedParams, formValues, type);
  return {
    General: {
      GlobalTransactionID: uuid(),
    },
    GeneralInsurance: {
      SessionKey: formValues[type][chosenPackage].sessionKey || '',
      EffectiveDate: effectiveDate,
      ProductGroupName: getDefaultOrValueFromObjects('productGroupName', mappedParams, formValues, product),
    },
    RolePlayerId: getValueFromObjects('rolePlayerId', mappedParams, formValues),
    PriorPolicy: {
      TerminateExistingInsurance: getValueFromObjects('terminateExistingInsurance', mappedParams, formValues),
      ClaimHistoryRequestForPriorInsurance: false,
      InsuranceCompanyCode: getValueFromObjects('terminateExternalPartyCode', mappedParams, formValues),
      WhenToTerminateExistingInsurance: 'Kort opsig',
      ExistingInsuranceTerminationConsent: getValueFromObjects('terminateExistingInsurance', mappedParams, formValues),
      ClaimHistoryConsent: getValueFromObjects('terminateExistingInsurance', mappedParams, formValues),
    },
    OtherDriverExpirence: {
      OtherDrivingExperienceProvided: false,
    },
    Payment: {
      AccountNumber: getValueFromObjects('bankAccountNumber', mappedParams, formValues),
      BankNumber: getValueFromObjects('bankRegistrationNumber', mappedParams, formValues),
    },
    ElectronicContact: {
      EmailAddress: getValueFromObjects('emailAddress', mappedParams, formValues),
      MobilePhoneNumber: getValueFromObjects('telephoneNumber', mappedParams, formValues),
      PromotionAccept: getDefaultOrValueFromObjects('perm_contactInfo_accept', mappedParams, formValues, false),
      SMSPermissionForPromotion: getDefaultOrValueFromObjects('perm_contactInfo_accept', mappedParams, formValues, false),
      DigitalCommunication: getDefaultOrValueFromObjects('perm_contactInfo_accept', mappedParams, formValues, false),
      PrimaryChannel: '01',
    },
    Consent: {
      DataExchangeWithPartnerConsent: getDefaultOrValueFromObjects('hasDataExchangeWithPartnerConsent', mappedParams, formValues, false),
      UnionMemberShipConsent: getDefaultOrValueFromObjects('hasUnionMemberShipConsent', mappedParams, formValues, false),
    },
  };
};

function getChosenPackage(mappedParams, formValues, type) {
  return getValueFromObjects('chosenPackage-' + type, mappedParams, formValues) ||
    getValueFromObjects('chosenPackage', mappedParams, formValues) ||
    '';
}

export const getPurchaseMappingV2 = (formValues, mappedParams, product, type) => {
  let effectiveDate = getEffectiveDate(formValues, mappedParams);
  const chosenPackage = getChosenPackage(mappedParams, formValues, type);
  return {
    general: {
      globalTransactionId: uuid(),
    },
    generalInsurance: {
      sessionKey: formValues[type][chosenPackage].sessionKey || '',
      effectiveDate,
      productGroupName: getDefaultOrValueFromObjects('productGroupName', mappedParams, formValues, product),
    },
    rolePlayerId: getValueFromObjects('rolePlayerId', mappedParams, formValues),
    priorPolicy: {
      terminateExistingInsurance: getValueFromObjects(`terminateExistingInsurance_${formValues.insuranceType}`, mappedParams, formValues),
      claimHistoryRequestForPriorInsurance: false,
      insuranceCompanyCode: getDefaultOrValueFromObjects('terminateExternalPartyCode', mappedParams, formValues, ''),
      whenToTerminateExistingInsurance: 'Kort opsig',
      existingInsuranceTerminationConsent: getValueFromObjects(`terminateExistingInsurance_${formValues.insuranceType}`, mappedParams, formValues),
      claimHistoryConsent: getValueFromObjects(`terminateExistingInsurance_${formValues.insuranceType}`, mappedParams, formValues),
    },
    payment: {
      accountNumber: getValueFromObjects('bankAccountNumber', mappedParams, formValues) || "",
      bankNumber: getValueFromObjects('bankRegistrationNumber', mappedParams, formValues) || "",
    },
    electronicContact: {
      emailAddress: getValueFromObjects('emailAddress', mappedParams, formValues),
      mobilePhoneNumber: getValueFromObjects('telephoneNumber', mappedParams, formValues),
      promotionAccept: getDefaultOrValueFromObjects('perm_contactInfo_accept', mappedParams, formValues, false),
      SMSPermissionForPromotion: getDefaultOrValueFromObjects('perm_contactInfo_accept', mappedParams, formValues, false),
      digitalCommunication: getDefaultOrValueFromObjects('perm_contactInfo_accept', mappedParams, formValues, false),
      primaryChannel: '01',
    },
    consent: {
      dataExchangeWithPartnerConsent: getDefaultOrValueFromObjects('hasDataExchangeWithPartnerConsent', mappedParams, formValues, false),
      unionMemberShipConsent: getDefaultOrValueFromObjects('hasUnionMemberShipConsent', mappedParams, formValues, false),
      marketingDataExchangeWithPartnerConsent: getDefaultOrValueFromObjects('hasMarketingDataExchangeWithPartnerConsent', mappedParams, formValues, false),
    },
    emailReceipt: getDefaultOrValueFromObjects('emailReceipt', mappedParams, formValues, false),
  };
};

class Product extends Api {
  static quotedCustomer = createAction(types.CUSTOMER_QUOTED);
  static quoteCustomer = createAction(types.CUSTOMER_QUOTE);
  static updatedQuote = createAction(types.UPDATED_QUOTE);
  static updateQuote = createAction(types.UPDATE_QUOTE);
  static createCustomer = createAction(types.CUSTOMER_CREATE);
  static createdCustomer = createAction(types.CUSTOMER_CREATED);
  static activationDateGet = createAction(types.CUSTOMER_GET_ACTIVATION_DATE);
  static activationDateReceived = createAction(types.CUSTOMER_RECEIVED_ACTIVATION_DATE);
  static activationDatesReceived = createAction(types.CUSTOMER_RECEIVED_ACTIVATION_DATES);
  static purchased = createAction(types.PURCHASED);
  static purchase = createAction(types.PURCHASE);

  static CREATE_CUSTOMER = 'car/v1/customer';
  static CREATE_CUSTOMER_PRODUCT = 'product/v1/customer';
  static PURCHASE = 'car/v1/purchasepolicy';
  static CAR_TJM_PAYMENT = 'tjmproduct/v1/payment';
  static PRODUCT_GROUPS = {
    'car': 'Personbil',
    'travel': 'Rejse',
    'household': 'Indbo',
    'pregnancy': 'Graviditet',
    'deposit': 'Tryg Leje',
  };

  constructor(type, createProductAction, updateAction, prepareAction) {
    super();
    this.type = type;
    this.createAction = createProductAction;
    this.updateAction = updateAction;
    this.prepareAction = prepareAction;
  }

  createCustomerAction(payload) {
    return dispatch => dispatch(Product.createCustomer(payload));
  }

  createdCustomerAction(payload) {
    return dispatch => dispatch(Product.createdCustomer(payload));
  }

  quotedCustomerAction(payload) {
    return dispatch => dispatch(Product.quotedCustomer(payload));
  }

  quoteCustomerAction() {
    return dispatch =>
      dispatch(Product.quoteCustomer());
  }

  updatedQuoteAction(payload) {
    return dispatch => dispatch(Product.updatedQuote(payload));
  }

  updateQuoteAction() {
    return (dispatch) =>
      dispatch(Promise.reject({}));
  }

  activationDateReceivedAction(payload) {
    return dispatch =>
      dispatch(Product.activationDateReceived(payload));
  }

  activationDatesReceivedAction(payload) {
    return dispatch =>
      dispatch(Product.activationDatesReceived(payload));
  }

  activationDateGetAction(formValues, baseModel, params) {
    return (dispatch) =>
      dispatch(this.requestGetActivationDate(baseModel)).then((data) => {
        const modData = {...data};
        if (formValues && formValues.setFieldValue) {
          Object.keys(data).forEach(key => {
            if(formValues.forProduct) {
              const keyData = `${key}-${formValues.forProduct}`;
              formValues.setFieldValue(keyData, data[key]);
              modData[keyData] = data[key];
            }else {
              formValues.setFieldValue(key, data[key]);
            }
          });
        }
        if (params && params.field === 'oneYearActivationDate') {
          const newData = getOneYearActivationDate(data);
          return dispatch(this.activationDateReceivedAction(newData));
        }
        if (params && params.field === 'petActivationDate') {
          const numberOfDays = params.days;
          const newData = getPetActivationDate(data, formValues, numberOfDays);
          return dispatch(this.activationDateReceivedAction(newData));
        }
        if (params && params.field === 'currentDate') {
          const newData = getCurrentDate();
          return dispatch(this.activationDateReceivedAction(newData));
        }

        return dispatch(this.activationDateReceivedAction(modData));
      }).catch(data => {
        dispatch(appErrorAction(data));

        return Promise.reject(data);
      });
  }

  purchaseAction() {
    return (dispatch) =>
      dispatch(Product.purchase());
  }

  purchasedAction(payload) {
    return dispatch =>
      dispatch(Product.purchased(payload));
  }

  shouldUpdate(state) {
    return state[`isUpdate${this.type}`] || true;
  }

  createProduct(service, parameters, mappedParams, formValues) {
    return dispatch => {
      const params = parameters;
      let id = null;
      if (Object.hasOwnProperty.call(params, 'packageId')) {
        const { packageId } = params;
        id = packageId;
        delete params.packageId;
      }

      return this.callApiPost(service, params, dispatch).then(data => {
        const newData = this.prepareCreateUpdateResponse(data, mappedParams, formValues);
        if (id) {
          newData.packageId = id;
        }
        return newData;
      });
    };
  }

  updateProduct(service, params) {
    return dispatch => {
      dispatch(this.updateAction());
      return this.callApiPatch(service, params, dispatch);
    };
  }

  /**
   * This method will be used call the all packages get price requests
   * @param service
   * @param requests
   * @param mappedParams
   * @param formValues
   * @param dispatch
   * @returns {Promise<unknown>}
   */
  callGetPriceRequests(service, requests, mappedParams, formValues, dispatch) {
    return new Promise((resolve, reject) => {
      dispatch(this.createProduct(service, requests[0], mappedParams, formValues))
        .then((first) => {
          if (requests.length > 1) {
            const serviceCalls = [];
            let delay = 0;
            requests.splice(1).forEach((request) => {
              serviceCalls.push(dispatch(new Promise((resolver) => {
                setTimeout(() => {
                  resolver(this.createProduct(service, request, mappedParams, formValues));
                }, delay);
              })));
              delay += 100;
            });
            Promise.all(serviceCalls)
              .then(data => {
                const response = {};
                if (data.length > 0) {
                  data.forEach(element => {
                    response[element.packageId] = element;
                  });
                }
                response[first.packageId] = first;
                resolve(response);
              })
              .catch(reject);
          } else {
            resolve({ [first.packageId]: first });
          }
        })
        .catch(reject);
    }).then(data => {
      dispatch(this.quotedCustomerAction(data));
      return data;
    });
  }

  requestCreateProduct(service, mappedParams, baseReqModel, formValues) {
    return (dispatch, getState) => {
      const state = getState();
      if (this.shouldUpdate(state)) {
        const requestModel = {
          General: {
            GlobalTransactionID: uuid(),
            PartnerCode: getValueFromObjects('partnerCode', mappedParams, formValues) || '',
            TrygConcept: getValueFromObjects('trygConcept', mappedParams, formValues) || 'TRPL',
            CampaignCode: getValueFromObjects('campaignCode', mappedParams, formValues) || [],
          },
          GeneralInsurance: {
            SessionKey: '',
            ProductGroupName: getValueFromObjects('productGroupName', mappedParams, formValues) || Product.PRODUCT_GROUPS[this.type],
            CustomerAgreement: getValueFromObjects('customerAgreement', mappedParams, formValues) || '',
          },
          Payment: {
            PaymentFrequency: getValueFromObjects(`paymentFrequency-${this.type}`, mappedParams, formValues),
            PaymentMethod: getValueFromObjects(`paymentMethod-${this.type}`, mappedParams, formValues),
          },
        };

        let packageInfo = getPackagesWithCoverage(state, mappedParams);
        dispatch(this.createAction());
        dispatch(this.quoteCustomerAction());

        if (packageInfo.length > 0) {
          const { userData: { packageId } } = state;
          if (packageId) {
            packageInfo = packageInfo.filter(item => item.packageId === packageId);
          }

          const requests = [];

          packageInfo.forEach(item => {
            let packageRequest = merge(baseReqModel, requestModel);

            if (formValues[this.type] && formValues[this.type][item.packageId]) {
              const updateReq = {
                General: {
                  ...packageRequest.General,
                  GlobalTransactionID: uuid(),
                },
                GeneralInsurance: {
                  ...packageRequest.GeneralInsurance,
                  SessionKey: formValues[this.type][item.packageId].sessionKey || '',
                },
              };
              packageRequest = { ...packageRequest, ...updateReq };
            } else {
              const updateReq = {
                General: {
                  ...packageRequest.General,
                  GlobalTransactionID: uuid(),
                },
              };
              packageRequest = { ...packageRequest, ...updateReq };
            }

            const coverage = item.coverageCodes.map((cov) => {
              return { CoverageCode: cov };
            });

            requests.push({
              ...packageRequest,
              ...item,
              Coverage: coverage,
            });
          });

          return this.callGetPriceRequests(service, requests
            , mappedParams, formValues, dispatch);
        }

        return dispatch(this.createProduct(service, ...merge(baseReqModel, requestModel),
          mappedParams, formValues))
          .then(data => {
            dispatch(this.quotedCustomerAction(data));
          });
      }

      return Promise.resolve();
    };
  }

  createCustomer(params, values, actionOnErrors = []) {
    return (dispatch, getState) => {
      try {
        const { formValues, mappedParams } = this.mapUserdata(getState(), values, params);
        const quote = 'quotes';
        const chosenPackage = getChosenPackage(mappedParams, formValues, this.type);
        const fullname = getValueFromObjects('fullName', mappedParams, formValues) || '';
        let firstName = getValueFromObjects('firstName', mappedParams, formValues);
        let lastName = getValueFromObjects('lastName', mappedParams, formValues);
        if( fullname !== '' ) {
          const names = fullname.split(' ');
          lastName = names[names.length - 1];
          firstName = names.splice(0, names.length - 1).join(' ');
        }
        const requestModel = {
          General: {
            GlobalTransactionID: uuid(),
          },
          GeneralInsurance: {
            SessionKey: formValues[quote][chosenPackage].sessionKey || '',
            ProductGroupName: getValueFromObjects('productGroupName', mappedParams, formValues) || Product.PRODUCT_GROUPS[formValues.basketList[0]],
          },
          Customer: {
            CivilRegistrationNumber: getCPRMapping(mappedParams, formValues),
            FirstName: firstName,
            LastName: lastName,
            EmailAddress: getValueFromObjects('emailAddress', mappedParams, formValues),
            TelephoneNumber: getValueFromObjects('telephoneNumber', mappedParams, formValues),
          },
          PostalAddress: getAddressMapping(mappedParams, formValues),
          Consent: {
            CPRConsent: true,
          },
        };

        dispatch(this.createCustomerAction());

        return this.callApiPost(Product.CREATE_CUSTOMER, requestModel, dispatch)
          .then((data) => {
            dispatch(this.createdCustomerAction(data));
            return data;
          })
          .catch(data => {
            dispatch(appErrorAction(data));
            if(actionOnErrors.length > 0)
              return this.handleError(actionOnErrors, data, getState, dispatch);
            return Promise.reject(data);
          });
      } catch (e) {
        return Promise.reject(e);
      }
    };
  }

  mapUserdata(state, values, params) {
    const formValues = { ...values, ...state.userData };
    const mappedParams = params
      ? createParams(params, formValues, state)
      : {};
    const errors = params ? validateParams(mappedParams, params) : [];
    if (errors.length > 0) {
      throw createErrorBody('Missing parameters', errors);
    }
    return { formValues, mappedParams };
  }

  createCustomerV2(params, values, actionOnErrors, productName) {
    return (dispatch, getState) => {
      try {
        const { formValues, mappedParams } = this.mapUserdata(getState(), values, params);
        const quote = 'quotes';
        const type = productName || formValues.forProduct || this.type;
        const chosenPackage = getChosenPackage(mappedParams, formValues, type );
        const fullname = getValueFromObjects('fullName', mappedParams, formValues) || '';
        let firstName = getValueFromObjects('firstName', mappedParams, formValues);
        let lastName = getValueFromObjects('lastName', mappedParams, formValues);
        if( fullname !== '' ) {
          const names = fullname.split(' ');
          lastName = names[names.length - 1];
          firstName = names.splice(0, names.length - 1).join(' ');
        }
        const requestModel = {
          general: {
            globalTransactionID: uuid(),
          },
          generalInsurance: {
            sessionKey: formValues[quote][chosenPackage].sessionKey || '',
            productGroupName: getValueFromObjects('productGroupName', mappedParams, formValues) || Product.PRODUCT_GROUPS[formValues.basketList[0]],
          },
          customer: {
            civilRegistrationNumber: getCPRMapping(mappedParams, formValues),
            firstName: firstName,
            lastName: lastName,
            emailAddress: getValueFromObjects('emailAddress', mappedParams, formValues),
            telephoneNumber: getValueFromObjects('telephoneNumber', mappedParams, formValues),
          },
          postalAddress: getAddressMappingV2(mappedParams, formValues),
          consent: {
            cprConsent: true,
          },
        };

        dispatch(this.createCustomerAction());

        return this.callApiPost(Product.CREATE_CUSTOMER_PRODUCT, requestModel, dispatch)
          .then((data) => {
            dispatch(this.createdCustomerAction(data));
            return data;
          })
          .catch(data => {
            dispatch(appErrorAction(data));
            if(actionOnErrors.length > 0)
              return this.handleError(actionOnErrors, data, getState, dispatch);
            return Promise.reject(data);
          });
      } catch (e) {
        return Promise.reject(e);
      }
    };
  }

  /**
   * This method used to parse get price request for new products with camel case requests
   * @param service
   * @param mappedParams
   * @param baseReqModel
   * @param formValues
   * @returns {function(*=, *): (Promise<unknown>)}
   */
  requestCreateProductV2(service, mappedParams, baseReqModel, formValues) {
    return (dispatch, getState) => {
      const state = getState();
      if (this.shouldUpdate(state)) {
        const { userData } = state;
        const requestModel = this.getRequestModel(mappedParams, formValues, userData);
        let packageInfo = getPackagesWithCoverage(state, mappedParams);
        dispatch(this.createAction());
        dispatch(this.quoteCustomerAction());
        if (packageInfo.length > 0) {
          const { userData: { packageId } } = state;
          if (packageId) {
            packageInfo = packageInfo.filter(item => item.packageId === packageId);
          }
          const requests = [];
          const quote = 'quotes';
          packageInfo.forEach(item => {
            const additionalPackageInfo = formValues.additionalPackagesInfo && formValues.additionalPackagesInfo.find(({ id }) => id === item.packageId);
            if (!formValues.forProduct || formValues.forProduct === additionalPackageInfo?.forProduct) {
              let packageRequest = merge(baseReqModel, requestModel);
              if (formValues[quote] && formValues[quote][item.packageId]) {
                const updateReq = {
                  general: {
                    ...packageRequest.general,
                    globalTransactionId: uuid(),
                  },
                  generalInsurance: {
                    ...packageRequest.generalInsurance,
                    sessionKey: formValues[quote][item.packageId].sessionKey || '',
                  },
                };
                packageRequest = { ...packageRequest, ...updateReq };
              } else {
                const updateReq = {
                  general: {
                    ...packageRequest.general,
                    globalTransactionId: uuid(),
                  },
                };
                packageRequest = { ...packageRequest, ...updateReq };
              }
              const coverage = item.coverageCodes.map((cov) => {
                return { coverageCode: cov };
              });
              // Reset travel value to default for Super and Udvidet packages if request have 0
              if (this.type === 'travel') {
                const updateTravelReq = {
                  travel: {
                    ...packageRequest.travel,
                    travelValue: getTravelValue(item.packageText, packageRequest),
                  },
                };
                packageRequest = { ...packageRequest, ...updateTravelReq };
              }
              requests.push({
                ...packageRequest,
                ...item,
                coverage,
                packageText: item.packageText,
              });
            }
          });
          return this.callGetPriceRequests(service, requests
            , mappedParams, formValues, dispatch);
        }
        return dispatch(this.createProduct(service, merge(baseReqModel, requestModel),
          mappedParams, formValues))
          .then(data => {
            dispatch(this.quotedCustomerAction(data));
          });
      }
      return Promise.resolve();
    };
  }

  getRequestModel(mappedParams, formValues, userData) {
    let customerAgreementvalue = getValueFromObjects('customerAgreement', mappedParams, formValues) || '';
    if(customerAgreementvalue === '' && formValues.discountCode !== '' && formValues.discountExclude
      && !formValues.discountExclude.includes(formValues.organizationCode) && formValues.age < formValues.discountAge) {
      customerAgreementvalue = formValues.discountCode;
    }
    return {
      general: {
        globalTransactionId: uuid(),
        partnerCode: getValueFromObjects('partnerCode', mappedParams, formValues) || '',
        trygConcept: getValueFromObjects('trygConcept', mappedParams, formValues) || 'TRPL',
        campaignCode: getValueFromObjects('campaignCode', mappedParams, formValues) || '',
      },
      generalInsurance: {
        sessionKey: '',
        productGroupName: getValueFromObjects('productGroupName', mappedParams, formValues) || Product.PRODUCT_GROUPS[formValues.basketList[0]],
        customerAgreement: customerAgreementvalue,
      },
      payment: {
        paymentFrequency: getValueFromObjects(`paymentFrequency-${this.type}`, mappedParams, formValues),
        paymentMethod: getValueFromObjects(`paymentMethod-${this.type}`, mappedParams, formValues),
      },
    };
  }

  getHouseHoldAddressRequestModel(mappedParams, formValues) {
    return {
      general: {
        globalTransactionId: uuid(),
        productGroupName: getValueFromObjects('productGroupName', mappedParams, formValues) || 'Indbo',
      },
      postalAddress: getAddressMappingV2(mappedParams, formValues),
      house: {
        ownershipType: getValueFromObjects('ownerShip', mappedParams, formValues) || null,
      },
    };
  }

  requestUpdateProduct() {
    return null;
  }

  getExcessAmount(formValues, baseRequestModel, dispatch) {
    const service = '/tryg/api/calcbuy/json/getExcessAmount';
    const reqModel = {
      customer: {
        civilRegistrationNumber: getCPRMapping({}, formValues) || ' ',
        age: getValueFromObjects('age', {}, formValues),
        claimCount: getValueFromObjects(`claimCount-${this.type}`, {}, formValues),
        consumerId: getValueFromObjects('consumerId', {}, formValues),
      },
    };
    return callApiPost(service, { ...reqModel, ...baseRequestModel }, dispatch);
  }

  getActivationDates(formValues, baseRequestModel, dispatch) {
    const service = '/tryg/api/calcbuy/json/activationDates';
    const reqModel = {
      customer: {
        civilRegistrationNumber: getCPRMapping({}, formValues),
        claimCount: getValueFromObjects(`claimCount-${this.type}`, {}, formValues),
        consumerId: getValueFromObjects('consumerId', {}, formValues),
      },
    };
    
    return callApiPost(
      service,
      { ...reqModel, ...baseRequestModel },
      dispatch,
      false,
      false,
      false
    ).then((data) => {
      const modData = {};
      if(formValues.forProduct) {
        const keyData = `activationdates-${formValues.forProduct}`;
        modData[`activationDate-${formValues.forProduct}`] = data[0].key;
        modData[`minActivationDate-${formValues.forProduct}`] = data[0].key;
        modData[`activationDateText-${formValues.forProduct}`] = data[0].value;
        formValues.setFieldValue(keyData, data);
        modData[keyData] = {...data};
        dispatch(this.activationDatesReceivedAction(modData));
      }
      return data;
    }).catch(data => {
      dispatch(appErrorAction(data));
      return Promise.reject(data);
    });
  }

  getActivationDatesMonthYear(formValues, baseRequestModel) {
    const reqModel = {
      customer: {
        civilRegistrationNumber: getCPRMapping({}, formValues),
        claimCount: getValueFromObjects(`claimCount-${this.type}`, {}, formValues),
        consumerId: getValueFromObjects('consumerId', {}, formValues),
      },
    };
    return Promise.resolve({
      ...getNext12MonthNamesWithYear(),
      usedParams: { ...reqModel, ...baseRequestModel },
    });
  }

  requestGetActivationDate(baseRequestModel) {
    return (dispatch, getState) => {
      if (this.shouldUpdate(getState())) {
        const { userData } = getState();
        const reqModel = {
          customer: {
            civilRegistrationNumber: getCPRMapping({}, userData),
            claimCount: getValueFromObjects(`claimCount-${this.type}`, {}, userData),
            consumerId: getValueFromObjects('consumerId', {}, userData),
          },
        };
        const service = '/tryg/api/calcbuy/json/activationDate';

        return callApiPost(service, { ...reqModel, ...baseRequestModel }, dispatch)
          .then(data => {
            if (!data.activationDate) {
              return Promise.reject('Missing date info.');
            }
            if (data.activationDate.key) {
              return {
                activationDate: data.activationDate.key,
                minActivationDate: data.activationDate.key,
                activationDateText: data.activationDate.value,
              };
            }
            return data;
          });
      }

      return Promise.resolve();
    };
  }

  /**
   *  Getting saved user Data from server
   * @returns {function(*=, *): Promise<{savedUserData: *}>}
   */
  saveUserData() {
    return (dispatch, getState) => {
      const stateData = getState();
      if (stateData.userData.loginStatus !== 'undefind') {
        delete stateData.userData.loginStatus;
      }
      if (stateData.userData.loginErrorCode !== 'undefind') {
        delete stateData.userData.loginErrorCode;
      }
      sessionStorage.setItem('stateData', JSON.stringify(stateData));
    };
  }

  /**
   * Getting parsed user state data from state key
   * @param stateToken
   * @returns {function(): Promise<unknown>}
   */
  getStateData(stateToken) {
    return () => {
      const params = {
        stateToken,
      };
      return decodeState(params, this.host).then((userData) => {
        return userData;
      });
    };
  }

  /*
  *  Getting user state data
  */
  getUserData() {
    return (dispatch, getState) => {
      if (this.shouldUpdate(getState())) {
        const service = '/api/json_page';
        return callApiGet(service, '', dispatch)
          .then(data => {
            return data;
          });
      }

      return Promise.resolve();
    };
  }

  purchase(mappedParams, baseReqModel, formValues = {}, actionOnErrors = []) {
    return (dispatch) => {
      const requestModel = getPurchaseMapping(
        formValues,
        mappedParams,
        Product.PRODUCT_GROUPS[this.type],
        this.type
      );
      this.purchaseAction();

      return this.callApiPost(Product.PURCHASE, merge(baseReqModel, requestModel), dispatch)
        .then((data) => {
          const policyNumber = data.policy.policyNumber;
          sessionStorage.setItem('policyNumber', policyNumber);
          dispatch(this.purchasedAction(policyNumber));
          return data;
        })
        .catch(data => {
          return this.handleError(actionOnErrors, data, getState, dispatch);
        });
    };
  }

  purchaseV2(service, mappedParams, baseReqModel, formValues = {}, actionOnErrors = []) {
    return (dispatch, getState) => {
      const requestModel = getPurchaseMappingV2(
        formValues,
        mappedParams,
        Product.PRODUCT_GROUPS[this.type],
        this.type
      );

      this.purchaseAction();

      return this.callApiPost(service, merge(baseReqModel, requestModel), dispatch)
        .then((data) => {
          const policyNumber = data.policy.policyNumber;
          sessionStorage.setItem('policyNumber', policyNumber);
          dispatch(this.purchasedAction(policyNumber));
          return data;
        })
        .catch(data => {
          return this.handleError(actionOnErrors, data, getState, dispatch);
        });
    };
  }

  prepareCreateUpdateResponse() {
  }

  getPriceAuxServiceMapping(payload) {
    const result = {};
    payload.auxiliaryList.forEach((item) => {
      const values = {};
      item.auxListValues.forEach((key) => {
        values[key] = key;
      });
      result[item.auxListName] = values;
    });
    return result;
  }

  getFeeAmountPerPayment(feeList, feeCode) {
    const feeAmountPerPaymentList =
      feeList &&
      feeList.filter(
        (fee) =>
          Object.prototype.hasOwnProperty.call(fee, 'feeCode') &&
          fee.feeCode &&
          fee.feeCode === feeCode
      );
    const feeAmountPerPayment =
      feeAmountPerPaymentList &&
        Object.prototype.hasOwnProperty.call(
          feeAmountPerPaymentList[0],
          'feeAmountPerPayment'
        )
        ? feeAmountPerPaymentList[0].feeAmountPerPayment
        : 0;

    return feeAmountPerPayment;
  }

  getErrorActions(actionOnErrors, data, packageData) {
    let result = false;
    if (actionOnErrors) {
      actionOnErrors.forEach((arrayItem => {
        if(!arrayItem.errorCodes) {
          result = packageData.errorActions[arrayItem.actionName];
        } else {
          arrayItem.errorCodes.forEach((errorCode => {
            if (errorCode === data.response.data.errorDetails.errorCode) {
              result = packageData.errorActions[arrayItem.actionName];
            }
          }));
        }
      }));
    }
    return result;
  }


  handleError(actionOnErrors, data, getState, dispatch) {
    let result;
    const actionOnError = this.getErrorActions(
      actionOnErrors, data, getState().section.packageData);
    if (actionOnError) {
      actionHandler(actionOnError.actions, null,
        null, dispatch, getState().userData);
      result = Promise.reject(createErrorBody('actionOnError', data));
    } else {
      dispatch(appErrorAction(data));
      result = Promise.reject(createErrorBody('appErrorAction', data));
    }
    return result;
  }
}

export default Product;